import { useState } from 'react';

const STORAGE_KEY = 'wm-payment-settings-v1';

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveSettings(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ configured }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[var(--wm-text-ui)] font-semibold ${
        configured
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${configured ? 'bg-green-500' : 'bg-gray-400'}`} />
      {configured ? 'Configured' : 'Not configured'}
    </span>
  );
}

// ── Stripe Card ───────────────────────────────────────────────────────────────

function StripeCard({ settings, onSave }) {
  const [publishableKey, setPublishableKey] = useState(settings.stripePublishableKey || '');
  const [testMode, setTestMode] = useState(settings.stripeTestMode !== false);
  const [saved, setSaved] = useState(false);

  const isConfigured = !!settings.stripePublishableKey;

  function handleSave() {
    onSave({ stripePublishableKey: publishableKey.trim(), stripeTestMode: testMode });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-xl p-5 space-y-4">
      {/* Card header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* Stripe icon */}
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#635bff' }}>
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Stripe</h3>
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Card payments via Stripe</p>
          </div>
        </div>
        <StatusBadge configured={isConfigured} />
      </div>

      {/* Publishable key */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">
          Publishable Key
        </label>
        <input
          type="text"
          value={publishableKey}
          onChange={e => setPublishableKey(e.target.value)}
          placeholder="pk_live_... or pk_test_..."
          className="w-full h-8 px-3 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[var(--wm-text-ui)] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]/30 font-mono transition-colors"
        />
        <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-1">
          Find your keys in the{' '}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer"
            className="text-blue-500 hover:underline">
            Stripe Dashboard
          </a>.
          The secret key is configured in Vercel environment variables (STRIPE_SECRET_KEY).
        </p>
      </div>

      {/* Test mode toggle */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Test Mode</p>
          <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">Use test keys (no real charges)</p>
        </div>
        <button
          type="button"
          onClick={() => setTestMode(v => !v)}
          className={`relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
            testMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-[#364860]'
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              testMode ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        className="w-full h-9 rounded-lg text-[var(--wm-text-body)] font-semibold text-white transition-colors"
        style={{ backgroundColor: saved ? '#22c55e' : 'var(--wm-nav-accent, #2E8BF0)' }}
      >
        {saved ? 'Saved!' : 'Save Stripe Settings'}
      </button>
    </div>
  );
}

// ── Square Card ───────────────────────────────────────────────────────────────

function SquareCard({ settings, onSave }) {
  const [appId, setAppId] = useState(settings.squareAppId || '');
  const [locationId, setLocationId] = useState(settings.squareLocationId || '');
  const [saved, setSaved] = useState(false);

  const isConfigured = !!(settings.squareAppId && settings.squareLocationId);

  function handleSave() {
    onSave({ squareAppId: appId.trim(), squareLocationId: locationId.trim() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-xl p-5 space-y-4">
      {/* Card header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-black">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.5 0h-11A6.5 6.5 0 000 6.5v11A6.5 6.5 0 006.5 24h11A6.5 6.5 0 0024 17.5v-11A6.5 6.5 0 0017.5 0zM16 15a1 1 0 01-1 1H9a1 1 0 01-1-1V9a1 1 0 011-1h6a1 1 0 011 1v6z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Square</h3>
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Payments via Square</p>
          </div>
        </div>
        <StatusBadge configured={isConfigured} />
      </div>

      {/* App ID */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">
          Application ID
        </label>
        <input
          type="text"
          value={appId}
          onChange={e => setAppId(e.target.value)}
          placeholder="sq0idp-..."
          className="w-full h-8 px-3 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[var(--wm-text-ui)] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]/30 font-mono transition-colors"
        />
      </div>

      {/* Location ID */}
      <div>
        <label className="block text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-1">
          Location ID
        </label>
        <input
          type="text"
          value={locationId}
          onChange={e => setLocationId(e.target.value)}
          placeholder="LxxxxxxxxxxxxxxxxX"
          className="w-full h-8 px-3 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[var(--wm-text-ui)] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]/30 font-mono transition-colors"
        />
        <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-1">
          Find your credentials in the{' '}
          <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer"
            className="text-blue-500 hover:underline">
            Square Developer Dashboard
          </a>.
        </p>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        className="w-full h-9 rounded-lg text-[var(--wm-text-body)] font-semibold text-white transition-colors"
        style={{ backgroundColor: saved ? '#22c55e' : 'var(--wm-nav-accent, #2E8BF0)' }}
      >
        {saved ? 'Saved!' : 'Save Square Settings'}
      </button>
    </div>
  );
}

// ── PaymentsTab ───────────────────────────────────────────────────────────────

export default function PaymentsTab() {
  const [settings, setSettings] = useState(() => loadSettings());

  function handleSave(updates) {
    const next = { ...settings, ...updates };
    setSettings(next);
    saveSettings(next);
  }

  return (
    <div className="space-y-5 max-w-xl">
      <div>
        <h2 className="text-[var(--wm-text-heading)] font-bold text-[#0F1923] dark:text-[#F8FAFE]">Payment Integrations</h2>
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">
          Configure payment processors to accept card payments directly in WrapMind.
          Credentials are stored locally and used only when processing payments.
        </p>
      </div>

      <StripeCard settings={settings} onSave={handleSave} />
      <SquareCard settings={settings} onSave={handleSave} />

      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
        style={{ background: 'rgba(100,116,139,0.07)', border: '1px solid rgba(100,116,139,0.2)' }}>
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] leading-relaxed">
          <strong>Security note:</strong> Never enter your Stripe <em>secret key</em> here. The secret key should be
          stored only in your Vercel project environment variables (STRIPE_SECRET_KEY). Only the publishable key is safe
          to store client-side.
        </p>
      </div>
    </div>
  );
}
